import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReduxLookups } from "hooks/useReduxLookups";
import { useReduxUser } from "hooks/useReduxUser";
import useDebounce from "hooks/useDebounce";
import { actions } from "store/actions";
import useNotify from "hooks/useNotify";

const useAgencyProjects = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails || {});
  const { notifyError } = useNotify();
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const { services } = useReduxLookups(false); // false for agency

  const agencyProjects = useSelector(
    (state) => state.agencyProjects || { results: [], count: 0 }
  );

  const serviceOptions = services.map((service) => ({
    ...service,
    value: service.id,
    label: service.title,
  }));

  const [filterQuery, setFilterQuery] = useState({
    filter: { op: "and", value: [] },
    paginate: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      setFilterQuery((prevQuery) => {
        const trimmedSearchTerm = debouncedSearchTerm.trim();

        const findOrderNumber = prevQuery.filter.value.find(
          (item) => item.key === "order_number"
        );

        let value = prevQuery.filter.value;
        if (findOrderNumber) {
          value = prevQuery.filter.value.map((item) => {
            if (item.key === "order_number") {
              item.value = trimmedSearchTerm;
            }
            return item;
          });
        } else {
          value.push({
            op: "lik",
            key: "order_number",
            value: trimmedSearchTerm,
          });
        }

        if (!trimmedSearchTerm) {
          value = value.filter((item) => item.key !== "order_number");
        }

        return {
          filter: {
            ...prevQuery.filter,
            value: value,
          },
          paginate: {
            ...prevQuery.paginate,
            pageNumber: 1,
          },
        };
      });
    }
  }, [debouncedSearchTerm]);

  const fetchProjects = useCallback(() => {
    // setIsLoading(true);
    dispatch(
      actions.getAgencyProjects(
        userDetails?.agencyId,
        filterQuery,
        (success, err) => {
          setIsLoading(false);
          if (!success) {
            notifyError(
              err?.response?.data?.message ??
                err?.message ??
                "Failed to fetch projects"
            );
          }
        }
      )
    );
  }, [dispatch, userDetails?.agencyId, filterQuery]);

  useEffect(() => {
    if (userDetails?.agencyId) {
      fetchProjects();
    }
  }, [filterQuery, userDetails?.agencyId]);

  const handlePageChange = useCallback((pageNumber) => {
    setFilterQuery((prevQuery) => ({
      ...prevQuery,
      paginate: {
        ...prevQuery.paginate,
        pageNumber,
      },
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setFilterQuery((prevQuery) => ({
      ...prevQuery,
      paginate: {
        pageSize,
        pageNumber: 1,
      },
    }));
  }, []);

  const clearFilters = () => {
    setSearchTerm(undefined);

    setSelectedServices([]);
    setSelectedDeadline(null);
    setFilterQuery({
      paginate: {
        pageSize: 10,
        pageNumber: 1,
      },
      filter: { op: "and", value: [] },
    });
  };

  return {
    searchTerm,
    setSearchTerm,
    selectedServices,
    setSelectedServices,
    selectedDeadline,
    setSelectedDeadline,

    serviceOptions,

    projects: agencyProjects.result,
    totalCount: agencyProjects.count,
    isLoading,

    pagination: filterQuery.paginate,
    handlePageChange,
    handlePageSizeChange,

    clearFilters,
    fetchProjects,
    filterQuery,
    setFilterQuery,
  };
};

export default useAgencyProjects;
